/**
 * Shared primitives for the agent pages (Supervisor, Engagement):
 * a settings ToggleRow, browser<->UTC hour helpers, a lightweight Tabs control,
 * and a debounced partial-workspace autosave hook.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { updateWorkspace } from '../../api';
import type { Workspace } from '../../types';
import './agents.css';

const MINUTES_PER_DAY = 24 * 60;

function normalizeHour(hour: number): number {
  return ((hour % 24) + 24) % 24;
}

export function utcHourToBrowserLocalHour(utcHour: number): number {
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  const localMinutes = utcHour * 60 - timezoneOffsetMinutes;
  const wrapped = ((localMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return normalizeHour(Math.floor(wrapped / 60));
}

export function browserLocalHourToUtcHour(localHour: number): number {
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  const utcMinutes = localHour * 60 + timezoneOffsetMinutes;
  const wrapped = ((utcMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return normalizeHour(Math.floor(wrapped / 60));
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (_value: boolean) => void;
  disabled?: boolean;
}

export function ToggleRow({ label, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="settings-toggle-row">
      <div className="settings-toggle-row__body">
        <div className="settings-toggle-row__label">{label}</div>
        <div className="settings-toggle-row__desc">{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={`settings-toggle${checked ? ' settings-toggle--on' : ''}`}
        onClick={() => onChange(!checked)}
        aria-label={label}
      >
        <span className="settings-toggle__thumb" />
      </button>
    </div>
  );
}

export type AgentTab = 'overview' | 'logs' | 'settings';

export function AgentTabs({
  active,
  onChange,
}: {
  active: AgentTab;
  onChange: (_tab: AgentTab) => void;
}) {
  const tabs: { id: AgentTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'logs', label: 'Logs' },
    { id: 'settings', label: 'Settings' },
  ];
  return (
    <div className="agent-tabs" role="tablist" aria-label="Agent sections">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={active === t.id}
          className={`agent-tab${active === t.id ? ' agent-tab--active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave for a partial workspace patch. Returns the current save
 * status and a `queueSave` function the settings UI calls after each change.
 */
export function useWorkspaceAutosave(
  workspaceId: number,
  onWorkspaceUpdate: (_ws: Workspace) => void,
  onError?: (_message: string) => void
) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingRef = useRef<Partial<Workspace>>({});

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      clearTimeout(savedTimerRef.current);
    },
    []
  );

  const flush = useCallback(async () => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(patch).length === 0) return;
    setSaveStatus('saving');
    try {
      const updated = await updateWorkspace(workspaceId, patch);
      onWorkspaceUpdate(updated);
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      setSaveStatus('error');
      onError?.(err?.message ?? 'Failed to save settings.');
    }
  }, [workspaceId, onWorkspaceUpdate, onError]);

  const queueSave = useCallback(
    (patch: Partial<Workspace>) => {
      pendingRef.current = { ...pendingRef.current, ...patch };
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 600);
    },
    [flush]
  );

  return { saveStatus, queueSave };
}

export function SaveStatusPill({ status }: { status: SaveStatus }) {
  if (status === 'idle') return null;
  const label =
    status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : 'Save failed';
  return <span className={`agent-save-pill agent-save-pill--${status}`}>{label}</span>;
}
